//Pedidos

import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { 
  Clock, CheckCircle, Truck, XCircle, Eye, Printer, 
  Package, MapPin, CreditCard, User, AlertCircle, Phone, Mail, Navigation,
  ReceiptText, ChevronLeft, ChevronRight, MessageCircle, Share2, Loader2, Edit3, Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import '../../styles/PedidosAtendimento.css'; 
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Importação das imagens dos ícones (para evitar que o marcador suma)
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Configuração do ícone padrão do Leaflet
let DefaultIcon = L.icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const Pedidos = ({ estabelecimentoId }) => {
  // Estados de Dados e Loading
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('pendente');
  
  // Estados de Modais e Detalhes
  const [pedidoDetalhado, setPedidoDetalhado] = useState(null);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [showModalCancel, setShowModalCancel] = useState(false);
  const [pedidoParaCancelar, setPedidoParaCancelar] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  
  // Estados de Exclusão
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [pedidoParaExcluir, setPedidoParaExcluir] = useState(null);
  
  // Logística e Rotas
  const [caminhoRota, setCaminhoRota] = useState([]); 
  const [showModalEntrega, setShowModalEntrega] = useState(false);
  const [pedidoParaEntrega, setPedidoParaEntrega] = useState(null);
  const [tipoLogistica, setTipoLogistica] = useState('propria');
  const [idPedidoExterno, setIdPedidoExterno] = useState('');
  
  // Estados de Mapa e Status Manual
  const [showModalMapa, setShowModalMapa] = useState(false);
  const [pedidoParaMapa, setPedidoParaMapa] = useState(null);   
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [activeCardTabs, setActiveCardTabs] = useState({}); 

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const pedidosPorPagina = 8;

  const ultimoPedidoIdRef = useRef(null);
  const audioNovoPedido = new Audio('https://assets.mixkit.co/active_storage/sfx/2257/2257-preview.mp3');

  // Fechar menu manual ao clicar fora
  useEffect(() => {
    const closeMenu = () => setShowStatusMenu(null);
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);

  const carregarPedidos = useCallback(async (isFirstLoad = false) => {
    try {
        const res = await api.get(`/pedidos/loja/${estabelecimentoId}?pedido_status=${filtroStatus}`);
        
        if (res && res.data) {
            const novosPedidos = Array.isArray(res.data) ? res.data : (res.data.payload || []);
            setPedidos(novosPedidos);
            
            if (novosPedidos.length > 0) {
                const maiorIdAtual = Math.max(...novosPedidos.map(p => p.id));
                if (!isFirstLoad && ultimoPedidoIdRef.current && maiorIdAtual > ultimoPedidoIdRef.current) {
                    audioNovoPedido.play().catch(e => {});
                    toast('🔔 Novo pedido recebido!');
                }
                ultimoPedidoIdRef.current = maiorIdAtual;
            }
        }
    } catch (err) {
        console.error("Erro ao carregar pedidos:", err);
        setPedidos([]);
    } finally {
        setLoading(false);
    }
  }, [estabelecimentoId, filtroStatus]);

  useEffect(() => {
    carregarPedidos(true);
    const interval = setInterval(() => carregarPedidos(false), 20000);
    return () => clearInterval(interval);
  }, [carregarPedidos]);

  // Cálculo da Rota Real seguindo as ruas (OSRM)
  const calcularRotaReal = async (origem, destino) => {
    try {
        const resp = await fetch(
            `https://router.project-osrm.org/route/v1/driving/${origem.lng},${origem.lat};${destino.lng},${destino.lat}?overview=full&geometries=geojson`
        );
        const data = await resp.json();
        if (data.routes && data.routes.length > 0) {
            const coordenadas = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
            setCaminhoRota(coordenadas);
        }
    } catch (e) {
        console.error("Erro ao buscar rota real:", e);
        setCaminhoRota([[origem.lat, origem.lng], [destino.lat, destino.lng]]);
    }
  };

  useEffect(() => {
    const latLoja = pedidoParaMapa?.lat_loja || pedidoParaMapa?.estabelecimento_lat;
    const lngLoja = pedidoParaMapa?.lng_loja || pedidoParaMapa?.estabelecimento_lng;
    const latCliente = pedidoParaMapa?.lat_cliente || pedidoParaMapa?.cliente_lat;
    const lngCliente = pedidoParaMapa?.lng_cliente || pedidoParaMapa?.cliente_lng;

    if (showModalMapa && latLoja && latCliente) {
        calcularRotaReal(
            { lat: parseFloat(latLoja), lng: parseFloat(lngLoja) },
            { lat: parseFloat(latCliente), lng: parseFloat(lngCliente) }
        );
    } else {
        setCaminhoRota([]);
    }
  }, [showModalMapa, pedidoParaMapa]);

  const totalPaginas = Math.ceil(pedidos.length / pedidosPorPagina);
  const pedidosExibidos = pedidos.slice((paginaAtual - 1) * pedidosPorPagina, paginaAtual * pedidosPorPagina);

  const handleTabChange = (pedidoId, tab) => {
    setActiveCardTabs(prev => ({ ...prev, [pedidoId]: tab }));
  };

  const alterarPedidoStatus = async (id, novoStatus) => {
    try {
      await api.put(`/pedidos/status-atendimento/${id}`, { pedido_status: novoStatus });
      toast.success(`Pedido movido para: ${novoStatus.toUpperCase()}`);
      carregarPedidos();
    } catch (err) {
      toast.error("Erro ao atualizar o pedido.");
    }
  };

  const marcarComoPago = async (id) => {
    try {
      await api.put(`/pedidos/marcar-pago/${id}`);
      toast.success("Pagamento confirmado e marcado como pago!");
      carregarPedidos();
    } catch (err) {
      toast.error("Erro ao atualizar status de pagamento.");
    }
  };

  const chamarEntregador = async () => {
    if (tipoLogistica === 'ifood' && !idPedidoExterno.trim()) {
      return toast.error("Insira o número do pedido do iFood");
    }
    try {
      const res = await api.post(`/pedidos/chamar-entregador/${pedidoParaEntrega}`, {
        tipo: tipoLogistica,
        pedido_externo: idPedidoExterno,
        estabelecimentoId
      });
      if (res.data.success) {
        toast.success(tipoLogistica === 'ifood' ? "Solicitação enviada ao iFood!" : "Entregador próprio notificado!");
        setShowModalEntrega(false);
        setIdPedidoExterno('');
        carregarPedidos(); 
      }
    } catch (err) {
      toast.error("Erro ao solicitar entregador.");
    }
  };

 const confirmarCancelamento = async () => {
    if (!motivoCancelamento.trim()) return toast.error("Por favor, insira um motivo.");
    try {
        await api.put(`/pedidos/status-atendimento/${pedidoParaCancelar}`, { 
          pedido_status: 'cancelado',
          motivo: motivoCancelamento 
        });
        
        toast.success("Pedido cancelado e cliente notificado.");
        setShowModalCancel(false);
        setMotivoCancelamento('');
        carregarPedidos();
    } catch (err) {
        toast.error("Erro ao cancelar pedido.");
    }
 };

  const confirmarExclusao = async () => {
    try {
        await api.delete(`/pedidos/${pedidoParaExcluir}`);
        toast.success("Pedido excluído com sucesso.");
        setShowModalDelete(false);
        setPedidoParaExcluir(null);
        carregarPedidos();
    } catch (err) {
        toast.error("Erro ao excluir pedido.");
    }
  };

  const verDetalhes = async (pedido) => {
    setLoadingDetalhes(true);
    setPedidoDetalhado(pedido);
    try {
      const res = await api.get(`/pedidos/detalhes/${pedido.id}`);
      if (res.data.success) {
        setPedidoDetalhado({ ...res.data.pedido, itens: res.data.itens });
      }
    } catch (err) {
      toast.error("Erro ao carregar detalhes");
    } finally {
      setLoadingDetalhes(false);
    }
  };

  return (
    <div className="pedidos-container" style={{ padding: '20px', background: '#f8fafc', minHeight: '100vh' }}>
      
      {/* HEADER E PAGINAÇÃO */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <h2 style={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Package size={28} /> Painel de Pedidos
        </h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', background: '#fff', padding: '10px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <button disabled={paginaAtual === 1} onClick={() => setPaginaAtual(p => p - 1)} className="btn-pag"><ChevronLeft size={20}/></button>
          <span style={{ fontWeight: 'bold' }}>{paginaAtual} de {totalPaginas || 1}</span>
          <button disabled={paginaAtual === totalPaginas || totalPaginas === 0} onClick={() => setPaginaAtual(p => p + 1)} className="btn-pag"><ChevronRight size={20}/></button>
        </div>
      </header>

      {/* TABS DE STATUS PRINCIPAL */}
      <div className="tabs-status" style={{ display: 'flex', gap: '10px', marginBottom: '25px', overflowX: 'auto' }}>
        {['pendente', 'preparando', 'entregando', 'concluido', 'cancelado'].map(st => (
          <button key={st} onClick={() => {setFiltroStatus(st); setPaginaAtual(1);}} className={`status-btn ${filtroStatus === st ? 'active' : ''}`}>
            {st === 'pendente' ? 'NOVOS' : st.toUpperCase()}
          </button>
        ))}
      </div>

      {/* GRID DE PEDIDOS */}
      {loading ? <div style={{ textAlign: 'center', padding: '50px' }}><Loader2 className="animate-spin" size={40}/></div> : (
        <div className="pedidos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
          {pedidosExibidos.map(ped => {
            const currentTab = activeCardTabs[ped.id] || 'pedido';
            const numeroZap = (ped.cliente_whatsapp || ped.whatsapp || '').replace(/\D/g, '');
            
            // Tratamento de campos de endereço flexíveis (evita campos vazios)
            const enderecoFormatado = ped.endereco || ped.endereco_entrega || ped.rua || 'Endereço não informado';
            const numeroFormatado = ped.numero || ped.numero_endereco || 'S/N';
            const bairroFormatado = ped.bairro ? `, ${ped.bairro}` : '';

            return (
              <div key={ped.id} className="pedido-card-interativo" style={{ background: '#fff', borderRadius: '16px', padding: '15px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '490px' }}>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '18px' }}>#{ped.id}</strong>
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button onClick={() => window.open(`https://wa.me/55${numeroZap}`)} className="mini-btn-zap" title="Chamar no WhatsApp"><MessageCircle size={14}/></button>
                    <button onClick={() => verDetalhes(ped)} className="mini-btn-print" title="Imprimir/Detalhes"><Printer size={14}/></button>
                  </div>
                </div>

                {/* Bloco Informativo de Pagamento e Cupom */}
                <div style={{ background: '#f8fafc', padding: '8px', borderRadius: '8px', marginBottom: '8px', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e2e8f0' }}>
                  <span>Forma: <strong>{ped.tipo_pagamento || 'Não informado'}</strong></span>
                  {parseFloat(ped.total || 0) === 0 ? (
                    <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                      🎟️ Cupom 100% (Grátis)
                    </span>
                  ) : (
                    <span style={{ color: ped.pago ? '#059669' : '#d97706', fontWeight: 'bold' }}>
                      {ped.pago ? '✅ Pago' : '⏳ Pendente'}
                    </span>
                  )}
                </div>

                {/* Botão para Marcar como Pago Manualmente (Caso seja Dinheiro e não esteja pago) */}
                {ped.tipo_pagamento?.toLowerCase() === 'dinheiro' && !ped.pago && parseFloat(ped.total || 0) > 0 && (
                  <button 
                    onClick={() => marcarComoPago(ped.id)} 
                    style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '6px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '8px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
                  >
                    💵 MARCAR COMO RECEBIDO (PAGO)
                  </button>
                )}

                <div className="card-internal-tabs" style={{ display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px', marginBottom: '12px' }}>
                  {['cliente', 'pedido', 'entrega'].map(t => (
                    <button key={t} onClick={() => handleTabChange(ped.id, t)} style={{ flex: 1, fontSize: '10px', padding: '5px', border: 'none', borderRadius: '6px', background: currentTab === t ? '#fff' : 'transparent', fontWeight: 'bold', cursor: 'pointer' }}>{t.toUpperCase()}</button>
                  ))}
                </div>

                <div style={{ flex: 1, fontSize: '13px', overflowY: 'auto' }}>
                  {currentTab === 'cliente' && (
                    <div className="animate-fade">
                      <p><strong>Nome:</strong> {ped.cliente_nome || ped.nome || 'Não informado'}</p>
                      <p><strong>WhatsApp:</strong> {ped.cliente_whatsapp || ped.whatsapp || 'Não informado'}</p>
                      <p><strong>Email:</strong> {ped.email || 'N/A'}</p>
                    </div>
                  )}

                  {currentTab === 'pedido' && (
                    <div className="animate-fade">
                      <div className="itens-preview-card" style={{ marginBottom: '12px' }}>
                        {ped.itens?.map((item, idx) => (
                          <div key={idx} style={{ padding: '6px 0', borderBottom: '1px dashed #f1f5f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
                              <span>{item.quantidade}x {item.nome}</span>
                              <span>R$ {(item.preco_unitario * item.quantidade).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: '5px' }}>
                        <strong>Total:</strong> <span style={{ color: '#059669', fontWeight: 'bold' }}>R$ {parseFloat(ped.total || 0).toFixed(2)}</span>
                      </p>
                    </div>
                  )}

                  {currentTab === 'entrega' && (
                    <div className="animate-fade">
                      <p><strong>Endereço:</strong> {enderecoFormatado}, {numeroFormatado}{bairroFormatado}</p>
                      <p><strong>Status:</strong> {ped.entregador_nome || 'Pendente'}</p>
                      <button onClick={() => { setPedidoParaMapa(ped); setShowModalMapa(true); }} style={{ marginTop: '10px', width: '100%', padding: '8px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer' }}>
                        <MapPin size={14} /> ACOMPANHAR MAPA
                      </button>
                    </div>
                  )}
                </div>

                {/* AÇÕES DO PEDIDO */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                  <div style={{ display: 'flex', gap: '5px', position: 'relative' }}>
                    {ped.pedido_status === 'pendente' && <button className="btn-status accept" style={{ flex: 2, background: '#22c55e', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => alterarPedidoStatus(ped.id, 'preparando')}>ACEITAR</button>}
                    {ped.pedido_status === 'preparando' && <button className="btn-status dispatch" style={{ flex: 2, background: '#3b82f6', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => { setPedidoParaEntrega(ped.id); setShowModalEntrega(true); }}>ENTREGA</button>}
                    {ped.pedido_status === 'entregando' && <button className="btn-status finish" style={{ flex: 2, background: '#10b981', color: '#fff', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => alterarPedidoStatus(ped.id, 'concluido')}>FINALIZAR</button>}

                    <button onClick={(e) => { e.stopPropagation(); setShowStatusMenu(showStatusMenu === ped.id ? null : ped.id); }} style={{ flex: 0.5, background: '#e2e8f0', border: 'none', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }}>
                      <Edit3 size={16} />
                    </button>

                    {showStatusMenu === ped.id && (
                      <div style={{ position: 'absolute', bottom: '110%', right: '0', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '8px', padding: '8px', zIndex: 100, width: '150px', border: '1px solid #e2e8f0' }}>
                        {['pendente', 'preparando', 'entregando', 'concluido', 'cancelado'].map(st => (
                          <button key={st} onClick={() => alterarPedidoStatus(ped.id, st)} style={{ width: '100%', padding: '8px', fontSize: '11px', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer' }}>{st.toUpperCase()}</button>
                        ))}
                      </div>
                    )}

                    <button className="btn-eye" style={{ flex: 0.5, background: '#f1f5f9', border: 'none', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer' }} onClick={() => verDetalhes(ped)}><Eye size={18}/></button>
                  </div>

                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button style={{ flex: 1, background: '#fff', color: '#dc2626', border: '1px solid #fee2e2', padding: '6px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => { setPedidoParaCancelar(ped.id); setShowModalCancel(true); }}>RECUSAR</button>
                    <button style={{ flex: 1, background: '#fee2e2', color: '#991b1b', border: 'none', padding: '6px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => { setPedidoParaExcluir(ped.id); setShowModalDelete(true); }}>EXCLUIR</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL ENTREGA LOGÍSTICA */}
      {showModalEntrega && (
        <div className="modal-cupom-overlay" onClick={() => setShowModalEntrega(false)}>
          <div className="modal-cupom-content" style={{ maxWidth: '450px', padding: '25px' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <Truck size={40} color="#3b82f6" style={{ margin: '0 auto' }} />
              <h3>Despachar Pedido #{pedidoParaEntrega}</h3>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button onClick={() => setTipoLogistica('propria')} style={{ flex: 1, padding: '15px', borderRadius: '12px', border: tipoLogistica === 'propria' ? '2px solid #3b82f6' : '1px solid #eee', background: tipoLogistica === 'propria' ? '#eff6ff' : '#fff', cursor: 'pointer' }}>Próprio</button>
              <button onClick={() => setTipoLogistica('ifood')} style={{ flex: 1, padding: '15px', borderRadius: '12px', border: tipoLogistica === 'ifood' ? '2px solid #ea1d2c' : '1px solid #eee', background: tipoLogistica === 'ifood' ? '#fff1f2' : '#fff', cursor: 'pointer' }}>iFood</button>
            </div>
            {tipoLogistica === 'ifood' && <input type="text" placeholder="ID Pedido iFood" value={idPedidoExterno} onChange={(e) => setIdPedidoExterno(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '20px' }} />}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowModalEntrega(false)} style={{ flex: 1, padding: '12px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>Voltar</button>
              <button onClick={chamarEntregador} style={{ flex: 1, padding: '12px', borderRadius: '8px', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>CHAMAR AGORA</button>
            </div>
          </div>
        </div>
      )}

      {/* 🗺️ MODAL DE RASTREIO FUNCIONAL (LEAFLET) */}
        {showModalMapa && pedidoParaMapa && (
          <div className="modal-cupom-overlay" onClick={() => setShowModalMapa(false)}>
            <div className="modal-cupom-content" style={{ maxWidth: '800px', width: '90%', padding: '0', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Rastreio Pedido #{pedidoParaMapa.id}</h3>
                <XCircle size={24} onClick={() => setShowModalMapa(false)} style={{ cursor: 'pointer', color: '#666' }} />
              </div>
          
              <div style={{ height: '450px', width: '100%', background: '#e5e7eb' }}>
                {(() => {
                  const latLoja = parseFloat(pedidoParaMapa.lat_loja || pedidoParaMapa.estabelecimento_lat || pedidoParaMapa.loja_lat);
                  const lngLoja = parseFloat(pedidoParaMapa.lng_loja || pedidoParaMapa.estabelecimento_lng || pedidoParaMapa.loja_lng);
                  
                  const latCliente = parseFloat(pedidoParaMapa.lat_cliente || pedidoParaMapa.cliente_lat || pedidoParaMapa.lat);
                  const lngCliente = parseFloat(pedidoParaMapa.lng_cliente || pedidoParaMapa.cliente_lng || pedidoParaMapa.lng);
        
                  if (!isNaN(latLoja) && !isNaN(lngLoja) && !isNaN(latCliente) && !isNaN(lngCliente)) {
                    return (
                      <MapContainer 
                        center={[latLoja, lngLoja]} 
                        zoom={13} 
                        style={{ height: "100%", width: "100%" }} >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        
                        <Marker position={[latLoja, lngLoja]}>
                          <Popup><strong>Loja:</strong> {pedidoParaMapa.loja_nome || 'Estabelecimento'}</Popup>
                        </Marker>
                    
                        <Marker position={[latCliente, lngCliente]}>
                          <Popup>
                              <div style={{ fontSize: '12px' }}>
                                  <strong>Cliente:</strong> {pedidoParaMapa.cliente_nome || pedidoParaMapa.nome}<br/>
                                  <strong>Endereço:</strong> {pedidoParaMapa.endereco || pedidoParaMapa.endereco_entrega}, {pedidoParaMapa.numero || 'S/N'}<br/>
                                  <button 
                                      onClick={() => window.open(`https://wa.me/55${(pedidoParaMapa.cliente_whatsapp || pedidoParaMapa.whatsapp || '').replace(/\D/g, '')}`)}
                                      style={{ marginTop: '5px', background: '#25d366', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
                                  >
                                      Chamar no Whats
                                  </button>
                              </div>
                          </Popup>
                        </Marker>
                    
                        {caminhoRota.length > 0 ? (
                            <Polyline 
                                positions={caminhoRota} 
                                color="#3b82f6" 
                                weight={5}
                                opacity={0.8}
                            />
                        ) : (
                            <Polyline 
                                positions={[
                                    [latLoja, lngLoja],
                                    [latCliente, lngCliente]
                                ]} 
                                color="#cbd5e1" 
                                dashArray="10, 10" 
                            />
                        )}
                      </MapContainer>
                    );
                  } else {
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '10px' }}>
                        <Navigation size={48} color="#94a3b8" className="animate-pulse" />
                        <p style={{ color: '#64748b' }}>Coordenadas incompletas ou não encontradas para este pedido.</p>
                        <span style={{ fontSize: '11px', color: '#999' }}>
                          Loja: {latLoja}, {lngLoja} | Cliente: {latCliente}, {lngCliente}
                        </span>
                      </div>
                    );
                  }
                })()}
              </div>
          
              <div style={{ padding: '20px', background: '#f8fafc', borderTop: '1px solid #eee' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Truck size={18} color="#3b82f6" />
                      <strong style={{ fontSize: '14px' }}>{pedidoParaMapa.entregador_nome || 'Entregador Próprio'}</strong>
                    </div>
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                      Status: <span style={{ color: '#0ea5e9', fontWeight: 'bold' }}>{pedidoParaMapa.pedido_status?.toUpperCase()}</span>
                    </p>
                  </div>
                  
                  {pedidoParaMapa.pedido_status === 'entregando' && (
                    <button 
                      onClick={() => { alterarPedidoStatus(pedidoParaMapa.id, 'concluido'); setShowModalMapa(false); }} 
                      style={{ background: '#10b981', color: '#fff', border: 'none', padding: '12px 25px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                    >
                      FINALIZAR ENTREGA
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* MODAL CANCELAMENTO */}
      {showModalCancel && (
        <div className="modal-cupom-overlay" onClick={() => setShowModalCancel(false)}>
          <div className="modal-cupom-content" style={{ maxWidth: '400px', padding: '25px' }} onClick={e => e.stopPropagation()}>
            <h3>Recusar Pedido #{pedidoParaCancelar}</h3>
            <textarea placeholder="Motivo..." value={motivoCancelamento} onChange={(e) => setMotivoCancelamento(e.target.value)} style={{ width: '100%', minHeight: '100px', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowModalCancel(false)} style={{ flex: 1, padding: '10px', border: 'none', cursor: 'pointer' }}>Voltar</button>
              <button onClick={confirmarCancelamento} style={{ flex: 1, padding: '10px', background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer' }}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EXCLUSÃO DE PEDIDO */}
      {showModalDelete && (
        <div className="modal-cupom-overlay" onClick={() => setShowModalDelete(false)}>
          <div className="modal-cupom-content" style={{ maxWidth: '400px', padding: '25px' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              <Trash2 size={40} color="#dc2626" style={{ margin: '0 auto' }} />
              <h3>Excluir Pedido #{pedidoParaExcluir}</h3>
            </div>
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
              Tem certeza que deseja remover este pedido permanentemente do painel? Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowModalDelete(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={confirmarExclusao} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: '#dc2626', color: '#fff', border: 'none', fontWeight: 'bold', cursor: 'pointer' }}>EXCLUIR</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALHES / CUPOM FISCAL */}
      {pedidoDetalhado && (
        <div className="modal-cupom-overlay" onClick={() => setPedidoDetalhado(null)}>
          <div className="modal-cupom-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '450px', padding: '20px', background: '#fff', borderRadius: '12px' }}>
            <div id="cupom-fiscal" style={{ fontFamily: 'monospace', fontSize: '12px', color: '#000', lineHeight: '1.4' }}>
              {/* CABEÇALHO DA LOJA */}
              <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <ReceiptText size={28} style={{ margin: '0 auto', display: 'block' }} />
                <h3 style={{ margin: '5px 0 2px 0', fontSize: '16px' }}>{pedidoDetalhado.loja_nome || 'Estabelecimento'}</h3>
                {pedidoDetalhado.loja_rua && (
                  <p style={{ margin: 0 }}>
                    {pedidoDetalhado.loja_rua}, {pedidoDetalhado.loja_numero || 'S/N'} {pedidoDetalhado.loja_bairro ? `- ${pedidoDetalhado.loja_bairro}` : ''}
                  </p>
                )}
                {pedidoDetalhado.loja_cidade && <p style={{ margin: 0 }}>{pedidoDetalhado.loja_cidade}</p>}
                {pedidoDetalhado.loja_whatsapp && <p style={{ margin: 0 }}>Tel/WhatsApp: {pedidoDetalhado.loja_whatsapp}</p>}
                {pedidoDetalhado.loja_email && <p style={{ margin: 0 }}>E-mail: {pedidoDetalhado.loja_email}</p>}
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

              <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                COMPROVANTE DE PEDIDO #{pedidoDetalhado.id}
              </div>
              <p style={{ margin: '3px 0' }}>Data: {new Date(pedidoDetalhado.criado_em || Date.now()).toLocaleString('pt-BR')}</p>
              
              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

              {/* DADOS DO CLIENTE E ENTREGA */}
              <p style={{ margin: '3px 0' }}><strong>Cliente:</strong> {pedidoDetalhado.cliente_nome || pedidoDetalhado.nome || 'Não informado'}</p>
              <p style={{ margin: '3px 0' }}><strong>WhatsApp:</strong> {pedidoDetalhado.cliente_whatsapp || pedidoDetalhado.whatsapp || 'Não informado'}</p>
              <p style={{ margin: '3px 0' }}><strong>Endereço de Entrega:</strong> {pedidoDetalhado.endereco || pedidoDetalhado.endereco_entrega || pedidoDetalhado.cliente_rua || 'N/A'}, {pedidoDetalhado.numero || pedidoDetalhado.cliente_numero || 'S/N'} {pedidoDetalhado.bairro ? `- ${pedidoDetalhado.bairro}` : ''}</p>
              <p style={{ margin: '3px 0' }}><strong>Tipo de Entrega:</strong> {pedidoDetalhado.tipo_entrega || 'Entrega a domicílio'}</p>
              {pedidoDetalhado.entregador_nome && <p style={{ margin: '3px 0' }}><strong>Entregador:</strong> {pedidoDetalhado.entregador_nome}</p>}

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

              {/* ITENS DO PEDIDO */}
              <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>ITENS DO PEDIDO:</div>
              {pedidoDetalhado.itens?.map((it, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>{it.quantidade}x {it.nome}</span>
                  <span>R$ {(it.preco_unitario * it.quantidade).toFixed(2)}</span>
                </div>
              ))}

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }}></div>

              {/* TOTAIS E PAGAMENTO */}
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                <span>Forma de Pagamento:</span>
                <span style={{ fontWeight: 'bold' }}>{pedidoDetalhado.tipo_pagamento || 'Não informado'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                <span>Status Pagamento:</span>
                <span style={{ fontWeight: 'bold' }}>{pedidoDetalhado.pago ? 'PAGO' : 'PENDENTE'}</span>
              </div>

              {/* SE HOUVER CUPOM APLICADO */}
              {pedidoDetalhado.cupom_id || parseFloat(pedidoDetalhado.desconto_valor || 0) > 0 || parseFloat(pedidoDetalhado.total || 0) === 0 ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0', color: '#047857' }}>
                  <span>Cupom Aplicado / Desconto:</span>
                  <span>- R$ {parseFloat(pedidoDetalhado.desconto_valor || 0).toFixed(2)} {parseFloat(pedidoDetalhado.total || 0) === 0 ? '(100% OFF)' : ''}</span>
                </div>
              ) : null}

              {parseFloat(pedidoDetalhado.valor_entrega || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '3px 0' }}>
                  <span>Taxa de Entrega:</span>
                  <span>R$ {parseFloat(pedidoDetalhado.valor_entrega).toFixed(2)}</span>
                </div>
              )}

              <div style={{ borderTop: '1px solid #000', margin: '10px 0', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                <span>TOTAL A PAGAR:</span>
                <span>R$ {parseFloat(pedidoDetalhado.total || 0).toFixed(2)}</span>
              </div>
            </div>

            <div style={{ padding: '20px 0 0 0', display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={() => window.print()} style={{ padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', background: '#3b82f6', color: '#fff', border: 'none', fontWeight: 'bold' }}>Imprimir</button>
              <button onClick={() => setPedidoDetalhado(null)} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pedidos;